/**
 * Commit Approval Queue Page
 *
 * Displays pending platform commits awaiting approval with approve/reject actions.
 * Used by platform administrators to review and approve/reject agent-generated commits.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 * API Pattern: Make_Authorized_API_Call with Tim-Combo response format
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  FiGitCommit,
  FiRefreshCw,
  FiCheck,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiAlertCircle,
  FiFile,
  FiGitBranch
} from 'react-icons/fi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';
import { useStatus } from '../../contexts/StatusProvider';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

interface FileChange {
  filename: string;
  status: 'added' | 'modified' | 'deleted' | 'renamed';
  additions: number;
  deletions: number;
  patch?: string;
}

interface PendingCommit {
  commit_id: string;
  repository: string;
  branch: string;
  agent_name: string;
  title: string;
  description?: string;
  files_changed: FileChange[];
  created_at: string;
  expires_at: string;
  status: 'pending' | 'approved' | 'rejected';
  created_by: string;
}

type ActionModalType = 'approve' | 'reject' | null;

// =============================================================================
// COMPONENT
// =============================================================================

const CommitApprovalQueue: React.FC = () => {
  const { Client_ID } = useStatus();

  // State
  const [pendingCommits, setPendingCommits] = useState<PendingCommit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [expandedCommitId, setExpandedCommitId] = useState<string | null>(null);

  // Action modal state
  const [actionModal, setActionModal] = useState<ActionModalType>(null);
  const [selectedCommit, setSelectedCommit] = useState<PendingCommit | null>(null);
  const [actionComment, setActionComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  // Get effective client ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');

  // =============================================================================
  // API CALLS
  // =============================================================================

  // Fetch pending commits from API
  const fetchPendingCommits = useCallback(async () => {
    if (!effectiveClientId) {
      setError('No client context available. Please select a client.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call<PendingCommit>(
        API_ENDPOINTS.PLATFORM_COMMITS.LIST,
        'GET',
        undefined,
        { params: { Client_ID: effectiveClientId, status: 'pending' } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch pending commits');
      }

      setPendingCommits(response.data.Records || []);
    } catch (err) {
      console.error('Error fetching pending commits:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pending commits');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveClientId]);

  // Approve a commit
  const approveCommit = useCallback(async (commitId: string, comment: string) => {
    setIsSubmitting(true);
    setActionError('');

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.PLATFORM_COMMITS.APPROVE,
        'POST',
        { commit_id: commitId, comment: comment || undefined }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to approve commit');
      }

      // Refresh the list
      setRefreshTrigger(prev => prev + 1);
      closeActionModal();
    } catch (err) {
      console.error('Error approving commit:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to approve commit');
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // Reject a commit
  const rejectCommit = useCallback(async (commitId: string, reason: string) => {
    if (!reason.trim()) {
      setActionError('Reason is required for rejection');
      return;
    }

    setIsSubmitting(true);
    setActionError('');

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.PLATFORM_COMMITS.REJECT,
        'POST',
        { commit_id: commitId, reason }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to reject commit');
      }

      // Refresh the list
      setRefreshTrigger(prev => prev + 1);
      closeActionModal();
    } catch (err) {
      console.error('Error rejecting commit:', err);
      setActionError(err instanceof Error ? err.message : 'Failed to reject commit');
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Initial fetch and on refresh trigger
  useEffect(() => {
    fetchPendingCommits();
  }, [fetchPendingCommits, refreshTrigger]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const toggleExpand = useCallback((commitId: string) => {
    setExpandedCommitId(prev => prev === commitId ? null : commitId);
  }, []);

  const openApproveModal = useCallback((commit: PendingCommit) => {
    setSelectedCommit(commit);
    setActionModal('approve');
    setActionComment('');
    setActionError('');
  }, []);

  const openRejectModal = useCallback((commit: PendingCommit) => {
    setSelectedCommit(commit);
    setActionModal('reject');
    setActionComment('');
    setActionError('');
  }, []);

  const closeActionModal = useCallback(() => {
    setActionModal(null);
    setSelectedCommit(null);
    setActionComment('');
    setActionError('');
  }, []);

  const handleActionSubmit = useCallback(() => {
    if (!selectedCommit) return;

    if (actionModal === 'approve') {
      approveCommit(selectedCommit.commit_id, actionComment);
    } else if (actionModal === 'reject') {
      rejectCommit(selectedCommit.commit_id, actionComment);
    }
  }, [actionModal, selectedCommit, actionComment, approveCommit, rejectCommit]);

  // =============================================================================
  // HELPERS
  // =============================================================================

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeUntilExpiry = (expiresAt: string): { text: string; isUrgent: boolean } => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) {
      return { text: 'Expired', isUrgent: true };
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h`, isUrgent: false };
    }

    if (hours > 0) {
      return { text: `${hours}h ${minutes}m`, isUrgent: hours < 6 };
    }

    return { text: `${minutes}m`, isUrgent: true };
  };

  const getFileChangesSummary = (files: FileChange[]): string => {
    const added = files.filter(f => f.status === 'added').length;
    const modified = files.filter(f => f.status === 'modified').length;
    const deleted = files.filter(f => f.status === 'deleted').length;

    const parts = [];
    if (added > 0) parts.push(`${added} added`);
    if (modified > 0) parts.push(`${modified} modified`);
    if (deleted > 0) parts.push(`${deleted} deleted`);

    return parts.join(', ') || 'No changes';
  };

  const getTotalChanges = (files: FileChange[]): { additions: number; deletions: number } => {
    return files.reduce(
      (acc, file) => ({
        additions: acc.additions + file.additions,
        deletions: acc.deletions + file.deletions
      }),
      { additions: 0, deletions: 0 }
    );
  };

  const getFileStatusColor = (status: string): string => {
    switch (status) {
      case 'added':
        return 'text-green-600 bg-green-50';
      case 'deleted':
        return 'text-red-600 bg-red-50';
      case 'modified':
        return 'text-yellow-600 bg-yellow-50';
      case 'renamed':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Pending count for badge
  const pendingCount = useMemo(() => pendingCommits.length, [pendingCommits]);

  // =============================================================================
  // RENDER: Loading State
  // =============================================================================

  if (isLoading && pendingCommits.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiGitCommit className="h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Commit Approval Queue</h1>
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading pending commits...</p>
          </div>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: Error State
  // =============================================================================

  if (error && pendingCommits.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <FiGitCommit className="h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">Commit Approval Queue</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <FiAlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: Main Content
  // =============================================================================

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FiGitCommit className="h-8 w-8 text-purple-600" />
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900">Commit Approval Queue</h1>
                {pendingCount > 0 && (
                  <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 rounded-full">
                    {pendingCount} pending
                  </span>
                )}
              </div>
              <p className="text-gray-600 mt-1">
                Review and approve platform commits generated by agents
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Refresh pending commits list"
          >
            <FiRefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Commits List */}
      <div className="space-y-4">
        {pendingCommits.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <FiGitCommit className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending approvals</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              All platform commits have been reviewed. New commits from agents will appear here when they require approval.
            </p>
          </div>
        ) : (
          /* Commit Cards */
          pendingCommits.map((commit) => {
            const isExpanded = expandedCommitId === commit.commit_id;
            const expiryInfo = getTimeUntilExpiry(commit.expires_at);
            const totalChanges = getTotalChanges(commit.files_changed);

            return (
              <div
                key={commit.commit_id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Commit Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      {/* Title and Agent */}
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {commit.title}
                        </h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {commit.agent_name}
                        </span>
                      </div>

                      {/* Description */}
                      {commit.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {commit.description}
                        </p>
                      )}

                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {/* Repository */}
                        <div className="flex items-center space-x-1 text-gray-600">
                          <FiGitCommit className="h-4 w-4" />
                          <span>{commit.repository}</span>
                        </div>

                        {/* Branch */}
                        <div className="flex items-center space-x-1 text-gray-600">
                          <FiGitBranch className="h-4 w-4" />
                          <span>{commit.branch}</span>
                        </div>

                        {/* Files Changed */}
                        <div className="flex items-center space-x-1 text-gray-600">
                          <FiFile className="h-4 w-4" />
                          <span>{getFileChangesSummary(commit.files_changed)}</span>
                        </div>

                        {/* Changes Summary */}
                        <div className="flex items-center space-x-2">
                          <span className="text-green-600 font-medium">+{totalChanges.additions}</span>
                          <span className="text-red-600 font-medium">-{totalChanges.deletions}</span>
                        </div>
                      </div>
                    </div>

                    {/* Expiry Badge */}
                    <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                      expiryInfo.isUrgent
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      <FiClock className="h-3 w-3" />
                      <span>Expires: {expiryInfo.text}</span>
                    </div>
                  </div>

                  {/* Footer Row */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Created: {formatDate(commit.created_at)}</span>
                      <span>ID: {commit.commit_id.slice(0, 8)}...</span>
                    </div>

                    <div className="flex items-center space-x-3">
                      {/* Expand/Collapse Button */}
                      <button
                        onClick={() => toggleExpand(commit.commit_id)}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                        aria-label={isExpanded ? 'Collapse diff view' : 'Expand diff view'}
                      >
                        {isExpanded ? (
                          <>
                            <FiChevronUp className="h-4 w-4" />
                            <span>Hide Changes</span>
                          </>
                        ) : (
                          <>
                            <FiChevronDown className="h-4 w-4" />
                            <span>View Changes</span>
                          </>
                        )}
                      </button>

                      {/* Reject Button */}
                      <button
                        onClick={() => openRejectModal(commit)}
                        className="inline-flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        aria-label={`Reject commit ${commit.title}`}
                      >
                        <FiX className="h-4 w-4" />
                        <span>Reject</span>
                      </button>

                      {/* Approve Button */}
                      <button
                        onClick={() => openApproveModal(commit)}
                        className="inline-flex items-center space-x-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                        aria-label={`Approve commit ${commit.title}`}
                      >
                        <FiCheck className="h-4 w-4" />
                        <span>Approve</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expandable Diff View */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-6">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">
                      Changed Files ({commit.files_changed.length})
                    </h4>
                    <div className="space-y-4">
                      {commit.files_changed.map((file, index) => (
                        <div key={index} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          {/* File Header */}
                          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getFileStatusColor(file.status)}`}>
                                {file.status.toUpperCase()}
                              </span>
                              <span className="font-mono text-sm text-gray-700">{file.filename}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="text-green-600">+{file.additions}</span>
                              <span className="text-red-600">-{file.deletions}</span>
                            </div>
                          </div>

                          {/* Diff Content */}
                          {file.patch && (
                            <pre className="p-4 text-xs font-mono overflow-x-auto bg-gray-900 text-gray-100">
                              {file.patch.split('\n').map((line, lineIndex) => {
                                let lineClass = 'text-gray-300';
                                if (line.startsWith('+') && !line.startsWith('+++')) {
                                  lineClass = 'text-green-400 bg-green-900/30';
                                } else if (line.startsWith('-') && !line.startsWith('---')) {
                                  lineClass = 'text-red-400 bg-red-900/30';
                                } else if (line.startsWith('@@')) {
                                  lineClass = 'text-blue-400';
                                }
                                return (
                                  <div key={lineIndex} className={`${lineClass} whitespace-pre`}>
                                    {line}
                                  </div>
                                );
                              })}
                            </pre>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Action Modal */}
      {actionModal && selectedCommit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50"
          onClick={closeActionModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-6 py-4 ${actionModal === 'approve' ? 'bg-green-600' : 'bg-red-600'}`}>
              <div className="flex items-center space-x-3 text-white">
                {actionModal === 'approve' ? (
                  <FiCheck className="h-6 w-6" />
                ) : (
                  <FiX className="h-6 w-6" />
                )}
                <h3 className="text-lg font-semibold">
                  {actionModal === 'approve' ? 'Approve Commit' : 'Reject Commit'}
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                {actionModal === 'approve'
                  ? 'You are about to approve this commit. This will trigger the commit to be pushed to the repository.'
                  : 'You are about to reject this commit. Please provide a reason for the rejection.'
                }
              </p>

              {/* Commit Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="font-medium text-gray-900">{selectedCommit.title}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedCommit.repository} / {selectedCommit.branch}
                </p>
              </div>

              {/* Comment/Reason Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {actionModal === 'approve' ? 'Comment (optional)' : 'Reason (required)'}
                </label>
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  placeholder={actionModal === 'approve'
                    ? 'Add an optional comment...'
                    : 'Explain why this commit is being rejected...'
                  }
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    actionModal === 'approve'
                      ? 'focus:ring-green-500'
                      : 'focus:ring-red-500'
                  } ${
                    actionError && actionModal === 'reject' && !actionComment.trim()
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* Error Message */}
              {actionError && (
                <div className="mt-3 flex items-center space-x-2 text-red-600 text-sm">
                  <FiAlertCircle className="h-4 w-4" />
                  <span>{actionError}</span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={closeActionModal}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleActionSubmit}
                disabled={isSubmitting || (actionModal === 'reject' && !actionComment.trim())}
                className={`inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 transition-colors ${
                  actionModal === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    {actionModal === 'approve' ? (
                      <FiCheck className="h-4 w-4" />
                    ) : (
                      <FiX className="h-4 w-4" />
                    )}
                    <span>{actionModal === 'approve' ? 'Approve' : 'Reject'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommitApprovalQueue;
